"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { ArrowRight, Loader2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { createContact } from "@/services/contact";
import { toast, Toaster } from "sonner";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { Reveal } from "@/shared/components/home";
import { ContactInfoPanel } from "./contact/ContactInfoPanel";
import { ContactSuccess } from "./contact/ContactSuccess";
import { FloatingField } from "./contact/FloatingField";
import { FloatingPhone } from "./contact/FloatingPhone";
import { FloatingTextarea } from "./contact/FloatingTextarea";
import "./contact/contact.css";

const formSchema = z.object({
  companyName: z.string().min(1, {
    message: "Şirkət adı daxil etmək məcburidir",
  }),
  email: z.string().email({
    message: "Düzgün email ünvanı daxil edin",
  }),
  phoneNumber: z.string().refine((val) => isValidPhoneNumber(val || "", "AZ"), {
    message: "Etibarlı Azərbaycan telefon nömrəsi daxil edin",
  }),
  subject: z.string().min(1, {
    message: "Mövzu daxil etmək məcburidir",
  }),
  message: z.string().min(1, {
    message: "Mesaj daxil etmək məcburidir",
  }),
});

function contactErrorMessage(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) return undefined;
  const response = (error as { response?: { data?: { message?: string } } }).response;
  return response?.data?.message;
}

export default function ContactSection() {
  const t = useTranslations("contact");
  const [submitted, setSubmitted] = useState(false);
  const [panelHeight, setPanelHeight] = useState<number>();
  const panelRef = useRef<HTMLDivElement>(null);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: "",
      email: "",
      phoneNumber: "",
      subject: "",
      message: "",
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createContact,
    onSuccess: (response) => {
      toast.success(response.message || "Mesajınız uğurla göndərildi!");
      if (panelRef.current) setPanelHeight(panelRef.current.offsetHeight);
      setSubmitted(true);
      form.reset();
    },
    onError: (error: unknown) => {
      toast.error(
        contactErrorMessage(error) || "Xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.",
      );
      console.error("Contact form error:", error);
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    mutate(values);
  }
  const { setValue, watch } = form;

  return (
    <section
      className="home-section w-full scroll-mt-24 bg-home-cream py-[72px] md:scroll-mt-20"
      id="contact"
    >
      <Toaster position="top-right" richColors />
      <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-12 px-8 lg:flex-row lg:items-stretch">
        <Reveal className="flex min-w-0 w-full flex-col lg:flex-[1_1_380px]">
          <ContactInfoPanel />
        </Reveal>

        <Reveal delay={80} className="mx-auto min-w-0 w-full max-w-[560px] lg:mx-0 lg:flex-[1_1_460px]">
          <div ref={panelRef}>
            {submitted ? (
              <ContactSuccess minHeight={panelHeight} onAgain={() => setSubmitted(false)} />
            ) : (
              <div className="flex flex-col gap-[18px]">
                <h3 className="text-[26px] font-bold text-home-navy">{t("apply")}</h3>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-[18px]">
                  <FloatingField
                  id="companyName"
                    label={t("company")}
                    error={form.formState.errors.companyName?.message}
                  {...form.register("companyName")}
                />
                  <FloatingField
                  id="email"
                  type="email"
                    label={t("email")}
                    error={form.formState.errors.email?.message}
                  {...form.register("email")}
                />
                  <FloatingPhone
                    id="phoneNumber"
                    label={t("phone")}
                    value={watch("phoneNumber")}
                    onChange={(value) => setValue("phoneNumber", value ?? "")}
                    error={form.formState.errors.phoneNumber?.message}
                  />
                  <FloatingField
                  id="subject"
                    label={t("subject")}
                    error={form.formState.errors.subject?.message}
                  {...form.register("subject")}
                />
                  <FloatingTextarea
                  id="message"
                    label={t("message")}
                    error={form.formState.errors.message?.message}
                  {...form.register("message")}
                />
                  <button
                    type="submit"
                    disabled={isPending}
                    className="flex h-[60px] w-full items-center justify-center gap-2 rounded-home-14 bg-home-navy text-[18px] font-bold text-home-cream hover:bg-home-navy-2 disabled:cursor-not-allowed disabled:bg-home-navy-2"
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                        {t("sending")}
                      </>
                    ) : (
                      <>
                        {t("send")}
                        <ArrowRight className="h-5 w-5" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
